import { useState } from "react";
import { CommandDialog } from "../../ui/command-dialog";
import { SelectField, TextAreaField, TextField } from "../../ui/fields";
import { describeCatalogError } from "../errors";
import type { Option } from "../labels";
import type { FieldErrors } from "../validation";

// One dialog for the three "create a lookup entry" flows (category, tag, person). A flow is a list of
// fields, a `build` that turns the typed values into a request body or into field errors (it uses the
// shared request schemas), and a `submit` that calls the API. A failed submit keeps the dialog open
// with the values (a refused field) or locks them (a lost answer) and a retry after a lost answer reuses the same idempotency key. It is a
// `CommandDialog`, which keeps one key for the life of the dialog.

export type EntityField = Readonly<{
  key: string;
  label: string;
  kind: "text" | "textarea" | "select";
  options?: readonly Option[];
  required?: boolean;
  hint?: string;
}>;

type Built<B> = Readonly<{ ok: true; body: B }> | Readonly<{ ok: false; errors: FieldErrors }>;

type CreateEntityDialogProps<B> = Readonly<{
  title: string;
  fields: readonly EntityField[];
  initial: Readonly<Record<string, string>>;
  build: (values: Readonly<Record<string, string>>) => Built<B>;
  submit: (body: B, idempotencyKey: string) => Promise<void>;
  /** Called after a successful create, with the body that was sent. */
  onCreated: (body: B, values: Readonly<Record<string, string>>) => void;
  onClose: () => void;
}>;

export function CreateEntityDialog<B>({
  title,
  fields,
  initial,
  build,
  submit,
  onCreated,
  onClose,
}: CreateEntityDialogProps<B>) {
  const [values, setValues] = useState<Readonly<Record<string, string>>>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});

  function validate(): boolean {
    const built = build(values);
    if (!built.ok) {
      setErrors(built.errors);
      return false;
    }
    setErrors({});
    return true;
  }

  return (
    <CommandDialog<B>
      title={title}
      confirmLabel="Create"
      pendingLabel="Creating…"
      order="confirm-first"
      validate={validate}
      mutate={async (idempotencyKey) => {
        const built = build(values);
        if (!built.ok) {
          setErrors(built.errors);
          return {
            ok: false,
            failure: { message: "Check the fields.", retryable: false, keepForm: true },
          };
        }
        try {
          await submit(built.body, idempotencyKey);
          return { ok: true, value: built.body };
        } catch (cause) {
          const described = describeCatalogError(cause);
          return {
            ok: false,
            failure: {
              message:
                described.kind === "conflict"
                  ? "An entry with this code or slug already exists. Search for it instead."
                  : described.message,
              retryable: described.retryable,
              keepForm: !described.retryable,
            },
          };
        }
      }}
      onDone={(body) => {
        onCreated(body, values);
        onClose();
      }}
      onClose={onClose}
    >
      {fields.map((field) => {
        const common = {
          id: `create-entity-${field.key}`,
          label: field.label,
          value: values[field.key] ?? "",
          onChange: (next: string) => setValues((current) => ({ ...current, [field.key]: next })),
          error: errors[field.key],
          ...(field.required === undefined ? {} : { required: field.required }),
          ...(field.hint === undefined ? {} : { hint: field.hint }),
        };
        if (field.kind === "select") {
          return <SelectField key={field.key} {...common} options={field.options ?? []} />;
        }
        if (field.kind === "textarea")
          return <TextAreaField key={field.key} {...common} rows={3} />;
        return <TextField key={field.key} {...common} />;
      })}
    </CommandDialog>
  );
}
